import React, { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useShallow } from 'zustand/react/shallow';
import { Cable, PlacedEquipment, Rack } from '../../types';
import { useProjectStore } from '../../store/projectStore';
import {
  BadgeAnchor,
  CableGeometryContext,
  brightenColor,
  extractPortNumber,
  getCableColor,
  getPortPosition,
  isPortPanelVisible,
  resolveCableEndpoints,
  flexiControlPoints,
  cablePathMidpoint,
  cableEndpointAttachment,
  isFlexiRouting,
  NAME_BADGE_FALLBACK_WIDTH,
  NAMEPLATE_ROW_PX,
} from '../../utils/cableGeometry';
import { canvasIconSizePx } from '../../utils/canvasIcon';
import { EDITOR_CELL_SIZE } from '../../utils/portLayout';
import { getPanningLayer, subscribeEquipmentDrag } from '../../utils/canvasDrag';
import { useLogicStore } from '../../store/logicStore';

interface LineEls {
  hit: HTMLElement | SVGSVGElement | null;
  fromCircle: HTMLElement | null;
  toCircle: HTMLElement | null;
}

interface ResolvedRow {
  cable: Cable;
  x1: number;
  y1: number;
  x2: number;
  y2: number;
  fromPlacedId: string;
  toPlacedId: string;
  sourcePanelOn: boolean;
  destPanelOn: boolean;
  fromPort: number | null;
  toPort: number | null;
  color: string;
  strokeWidth: number;
  hoverWidth: number;
  opacity: number;
  hoverOpacity: number;
  flexi: boolean;
}

function segmentGeometry(x1: number, y1: number, x2: number, y2: number) {
  const dx = x2 - x1;
  const dy = y2 - y1;
  return {
    len: Math.max(Math.hypot(dx, dy), 1),
    angle: Math.atan2(dy, dx) * (180 / Math.PI),
  };
}

function cableHoverName(cable: Cable): string {
  const named = cable.label?.trim();
  const isAutoPortName = named ? /^port\s+\d+$/i.test(named) : false;
  if (named && !isAutoPortName) return named;
  if (cable.subtype?.trim()) return cable.subtype.trim();
  const type = cable.type || 'ethernet';
  return `${type.charAt(0).toUpperCase()}${type.slice(1)} Cable`;
}

function paintCableNameChip(
  ctx: CanvasRenderingContext2D,
  row: ResolvedRow,
  left: number,
  top: number,
  zoom: number,
  iconHalf: number
) {
  const attach = rowAttachmentPoints(row, iconHalf);
  const mid = cablePathMidpoint(attach.from, attach.to, row.flexi);
  const x = mid.x * zoom + left;
  const y = mid.y * zoom + top;
  const text = cableHoverName(row.cable);
  ctx.font = '600 12px ui-sans-serif, system-ui, sans-serif';
  ctx.textAlign = 'center';
  ctx.textBaseline = 'middle';
  const padX = 12;
  const padY = 6;
  const w = Math.ceil(ctx.measureText(text).width) + padX * 2;
  const h = 12 + padY * 2;
  const rx = x - w / 2;
  const ry = y - h - 10;
  ctx.beginPath();
  if (typeof ctx.roundRect === 'function') {
    ctx.roundRect(rx, ry, w, h, 8);
  } else {
    ctx.rect(rx, ry, w, h);
  }
  ctx.fillStyle = '#1e293b';
  ctx.shadowColor = 'rgba(0, 0, 0, 0.35)';
  ctx.shadowBlur = 8;
  ctx.shadowOffsetY = 2;
  ctx.fill();
  ctx.shadowColor = 'transparent';
  ctx.shadowBlur = 0;
  ctx.shadowOffsetY = 0;
  ctx.fillStyle = '#ffffff';
  ctx.fillText(text, x, ry + h / 2);
}

function paintAttachPip(
  ctx: CanvasRenderingContext2D,
  point: { x: number; y: number },
  color: string,
  left: number,
  top: number,
  zoom: number,
  hovered: boolean
) {
  const x = point.x * zoom + left;
  const y = point.y * zoom + top;
  const r = hovered ? 4.75 : 4;
  ctx.beginPath();
  ctx.arc(x, y, r, 0, Math.PI * 2);
  ctx.fillStyle = hovered ? brightenColor(color, 30) : color;
  ctx.globalAlpha = 1;
  ctx.fill();
  ctx.lineWidth = 1;
  ctx.strokeStyle = 'rgba(255,255,255,0.85)';
  ctx.stroke();
}

function paintCables(
  canvas: HTMLCanvasElement | null,
  rows: ResolvedRow[],
  hoverId: string | null
) {
  if (!canvas) return;
  const viewport = canvas.parentElement;
  const cssW = Math.max(1, viewport?.clientWidth || canvas.clientWidth || 1);
  const cssH = Math.max(1, viewport?.clientHeight || canvas.clientHeight || 1);
  if (canvas.width !== cssW || canvas.height !== cssH) {
    canvas.width = cssW;
    canvas.height = cssH;
  }
  const ctx = canvas.getContext('2d', { alpha: true });
  if (!ctx) return;
  ctx.clearRect(0, 0, canvas.width, canvas.height);
  if (rows.length === 0) return;
  const { left, top, zoom } = readLivePanZoom();
  const iconHalf = canvasIconSizePx(
    useProjectStore.getState().gridSize,
    useProjectStore.getState().iconGridCells
  ) / 2;
  ctx.save();
  ctx.lineCap = 'round';
  for (const row of rows) {
    const hovered = hoverId === row.cable.id;
    const attach = rowAttachmentPoints(row, iconHalf);
    ctx.beginPath();
    const sx = attach.from.x * zoom + left;
    const sy = attach.from.y * zoom + top;
    const ex = attach.to.x * zoom + left;
    const ey = attach.to.y * zoom + top;
    ctx.moveTo(sx, sy);
    if (row.flexi) {
      const { c1, c2 } = flexiControlPoints(attach.from, attach.to);
      ctx.bezierCurveTo(
        c1.x * zoom + left,
        c1.y * zoom + top,
        c2.x * zoom + left,
        c2.y * zoom + top,
        ex,
        ey
      );
    } else {
      ctx.lineTo(ex, ey);
    }
    ctx.strokeStyle = hovered ? brightenColor(row.color, 30) : row.color;
    ctx.globalAlpha = hovered ? row.hoverOpacity : row.opacity;
    ctx.setLineDash([]);
    ctx.lineWidth = hovered ? row.hoverWidth : row.strokeWidth;
    ctx.stroke();
  }

  // Cut the line out of icon faces so endpoints sit behind them.
  // Nameplates overlap the port panel; only punch the part above the panel so
  // cables still reach the port squares.
  const origin = viewport?.getBoundingClientRect();
  if (origin) {
    ctx.globalCompositeOperation = 'destination-out';
    ctx.globalAlpha = 1;
    const punch = (x: number, y: number, w: number, h: number) => {
      if (w <= 0 || h <= 0) return;
      if (typeof ctx.roundRect === 'function') {
        ctx.beginPath();
        ctx.roundRect(x, y, w, h, 6);
        ctx.fill();
      } else {
        ctx.fillRect(x, y, w, h);
      }
    };
    document.querySelectorAll<HTMLElement>('[data-equipment-face]').forEach((el) => {
      const r = el.getBoundingClientRect();
      if (r.width <= 0 || r.height <= 0) return;
      const pad = 1;
      punch(r.left - origin.left - pad, r.top - origin.top - pad, r.width + pad * 2, r.height + pad * 2);
    });
    document.querySelectorAll<HTMLElement>('[data-equipment-nameplate]').forEach((el) => {
      const r = el.getBoundingClientRect();
      if (r.width <= 0 || r.height <= 0) return;
      const host = el.closest('[data-equipment-id]');
      const panel = host?.querySelector('[data-port-stage-for]') as HTMLElement | null;
      const panelTop = panel?.getBoundingClientRect().top ?? Number.POSITIVE_INFINITY;
      const visibleHeight = Math.min(r.height, Math.max(0, panelTop - r.top));
      if (visibleHeight <= 1) return;
      const pad = 1;
      punch(r.left - origin.left - pad, r.top - origin.top - pad, r.width + pad * 2, visibleHeight + pad);
    });
  }

  // Attachment pips sit on the port/badge edge after punch so they stay visible.
  ctx.globalCompositeOperation = 'source-over';
  ctx.globalAlpha = 1;
  for (const row of rows) {
    const hovered = hoverId === row.cable.id;
    const attach = rowAttachmentPoints(row, iconHalf);
    paintAttachPip(ctx, attach.from, row.color, left, top, zoom, hovered);
    paintAttachPip(ctx, attach.to, row.color, left, top, zoom, hovered);
  }

  if (hoverId && !useProjectStore.getState().isEquipmentDragActive) {
    const hoveredRow = rows.find((row) => row.cable.id === hoverId);
    if (hoveredRow) paintCableNameChip(ctx, hoveredRow, left, top, zoom, iconHalf);
  }
  ctx.restore();
}

function restorePortNumberSources() {
  document.querySelectorAll<HTMLElement>('[data-port-number-label]').forEach((el) => {
    el.style.visibility = '';
  });
}

/** Copy port numbers above the cable canvas so the stroke stays continuous underneath. */
function syncPortNumberOverlay(
  overlay: HTMLElement | null,
  viewport: HTMLElement | null,
  enabled: boolean
) {
  if (!overlay) return;
  if (!enabled || !viewport) {
    overlay.replaceChildren();
    restorePortNumberSources();
    return;
  }
  const origin = viewport.getBoundingClientRect();
  restorePortNumberSources();
  const sources = Array.from(
    viewport.querySelectorAll<HTMLElement>('[data-equipment-id] [data-port-number-label]')
  );
  while (overlay.childElementCount > sources.length) {
    overlay.lastElementChild?.remove();
  }
  sources.forEach((src, i) => {
    src.style.visibility = 'hidden';
    let node = overlay.children[i] as HTMLElement | undefined;
    if (!node) {
      node = document.createElement('div');
      overlay.appendChild(node);
    }
    const r = src.getBoundingClientRect();
    const cs = getComputedStyle(src);
    const layoutW = Math.max(src.offsetWidth, 1);
    const layoutH = Math.max(src.offsetHeight, 1);
    node.style.position = 'absolute';
    node.style.left = `${r.left - origin.left}px`;
    node.style.top = `${r.top - origin.top}px`;
    node.style.width = `${layoutW}px`;
    node.style.height = `${layoutH}px`;
    node.style.transformOrigin = 'top left';
    node.style.transform = `scale(${r.width / layoutW}, ${r.height / layoutH})`;
    node.style.display = 'flex';
    node.style.flexDirection = 'column';
    node.style.alignItems = 'center';
    node.style.justifyContent = 'flex-start';
    node.style.pointerEvents = 'none';
    node.style.fontSize = cs.fontSize;
    node.style.fontWeight = cs.fontWeight;
    node.style.fontFamily = cs.fontFamily;
    node.style.color = cs.color;
    node.style.lineHeight = cs.lineHeight;
    node.style.letterSpacing = cs.letterSpacing;
    node.style.gap = cs.gap;
    node.style.whiteSpace = 'nowrap';
    node.style.textShadow = '0 0 2px rgba(15, 23, 42, 0.95), 0 1px 2px rgba(15, 23, 42, 0.9)';
    if (node.innerHTML !== src.innerHTML) node.innerHTML = src.innerHTML;
  });
}

function endHalfSize(panelOn: boolean, port: number | null, _iconHalf: number): { w: number; h: number } {
  if (panelOn && port !== null) {
    const half = EDITOR_CELL_SIZE / 2;
    return { w: half, h: half };
  }
  return { w: NAME_BADGE_FALLBACK_WIDTH / 2, h: NAMEPLATE_ROW_PX / 2 };
}

/** Move cable ends from port/badge centers onto the edge facing the other end (NetBox-style). */
function rowAttachmentPoints(row: ResolvedRow, iconHalf: number): { from: { x: number; y: number }; to: { x: number; y: number } } {
  const fromHalf = endHalfSize(row.sourcePanelOn, row.fromPort, iconHalf);
  const toHalf = endHalfSize(row.destPanelOn, row.toPort, iconHalf);
  return {
    from: cableEndpointAttachment(
      { x: row.x1, y: row.y1 },
      { x: row.x2, y: row.y2 },
      fromHalf.w,
      fromHalf.h
    ),
    to: cableEndpointAttachment(
      { x: row.x2, y: row.y2 },
      { x: row.x1, y: row.y1 },
      toHalf.w,
      toHalf.h
    ),
  };
}

function placeDot(el: HTMLElement | null, x: number, y: number, size: number) {
  if (!el) return;
  el.style.left = `${x - size / 2}px`;
  el.style.top = `${y - size / 2}px`;
  el.style.width = `${size}px`;
  el.style.height = `${size}px`;
}

function setLineCoords(
  els: LineEls | undefined,
  x1: number,
  y1: number,
  x2: number,
  y2: number,
  flexi = false,
  attach?: { from: { x: number; y: number }; to: { x: number; y: number } }
) {
  if (!els) return;
  const a1 = attach?.from ?? { x: x1, y: y1 };
  const a2 = attach?.to ?? { x: x2, y: y2 };
  if (flexi) applyFlexiHit(els.hit, a1.x, a1.y, a2.x, a2.y);
  else applyHitSegment(els.hit, a1.x, a1.y, a2.x, a2.y);
  placeDot(els.fromCircle, a1.x, a1.y, els.fromCircle?.dataset.dragging === 'true' ? 12 : 8);
  placeDot(els.toCircle, a2.x, a2.y, els.toCircle?.dataset.dragging === 'true' ? 12 : 8);
}

function applyHitSegment(el: HTMLElement | SVGSVGElement | null, x1: number, y1: number, x2: number, y2: number) {
  if (!el) return;
  const { len, angle } = segmentGeometry(x1, y1, x2, y2);
  el.style.left = `${x1}px`;
  el.style.top = `${y1}px`;
  el.style.width = `${len}px`;
  el.style.transform = `rotate(${angle}deg)`;
  el.dataset.x1 = String(x1);
  el.dataset.y1 = String(y1);
  el.dataset.x2 = String(x2);
  el.dataset.y2 = String(y2);
}

/** Padding so the hit stroke stays inside the SVG box. */
const FLEXI_HIT_STROKE = 22;
const FLEXI_HIT_PAD = FLEXI_HIT_STROKE / 2 + 4;

function flexiHitMetrics(x1: number, y1: number, x2: number, y2: number) {
  const { c1, c2 } = flexiControlPoints({ x: x1, y: y1 }, { x: x2, y: y2 });
  const minX = Math.min(x1, x2, c1.x, c2.x) - FLEXI_HIT_PAD;
  const minY = Math.min(y1, y2, c1.y, c2.y) - FLEXI_HIT_PAD;
  const maxX = Math.max(x1, x2, c1.x, c2.x) + FLEXI_HIT_PAD;
  const maxY = Math.max(y1, y2, c1.y, c2.y) + FLEXI_HIT_PAD;
  return {
    minX,
    minY,
    width: Math.max(1, maxX - minX),
    height: Math.max(1, maxY - minY),
    d: `M ${x1 - minX} ${y1 - minY} C ${c1.x - minX} ${c1.y - minY}, ${c2.x - minX} ${c2.y - minY}, ${x2 - minX} ${y2 - minY}`,
  };
}

function applyFlexiHit(el: HTMLElement | SVGSVGElement | null, x1: number, y1: number, x2: number, y2: number) {
  if (!el) return;
  const m = flexiHitMetrics(x1, y1, x2, y2);
  el.style.left = `${m.minX}px`;
  el.style.top = `${m.minY}px`;
  el.style.width = `${m.width}px`;
  el.style.height = `${m.height}px`;
  el.style.transform = 'none';
  el.style.marginTop = '0';
  if (el instanceof SVGSVGElement) {
    el.setAttribute('viewBox', `0 0 ${m.width} ${m.height}`);
  }
  el.setAttribute('data-x1', String(x1));
  el.setAttribute('data-y1', String(y1));
  el.setAttribute('data-x2', String(x2));
  el.setAttribute('data-y2', String(y2));
  const path = el.querySelector('path');
  if (path) path.setAttribute('d', m.d);
}

function flexiHitStyle(x1: number, y1: number, x2: number, y2: number): React.CSSProperties {
  const m = flexiHitMetrics(x1, y1, x2, y2);
  return {
    position: 'absolute',
    left: m.minX,
    top: m.minY,
    width: m.width,
    height: m.height,
    overflow: 'visible',
    pointerEvents: 'none',
  };
}

function segmentStyle(x1: number, y1: number, x2: number, y2: number, height: number): React.CSSProperties {
  const { len, angle } = segmentGeometry(x1, y1, x2, y2);
  return {
    position: 'absolute',
    left: x1,
    top: y1,
    width: len,
    height,
    marginTop: -height / 2,
    transformOrigin: '0 50%',
    transform: `rotate(${angle}deg)`,
    cursor: 'pointer',
    pointerEvents: 'auto',
    background: 'transparent',
  };
}

function paintEndpointCircle(
  circle: HTMLElement | null,
  opts: { dragging: boolean; hoverPort: boolean; color: string }
) {
  if (!circle) return;
  const size = opts.dragging ? 12 : 10;
  circle.style.width = `${size}px`;
  circle.style.height = `${size}px`;
  circle.style.opacity = opts.dragging ? '1' : '0.9';
  circle.style.border = opts.dragging && opts.hoverPort ? '2px solid #00ff00' : `${opts.dragging ? 2 : 1.5}px solid #ffffff`;
  circle.style.background = opts.dragging && opts.hoverPort ? brightenColor(opts.color, 50) : opts.color;
  circle.style.cursor = opts.dragging ? 'grabbing' : 'grab';
  if (opts.dragging) circle.dataset.dragging = 'true';
  else delete circle.dataset.dragging;
}

function readLivePanZoom() {
  const layer = getPanningLayer();
  const zoom = useProjectStore.getState().canvasZoom || 1;
  if (!layer) return { left: 0, top: 0, zoom };
  return {
    left: parseFloat(layer.style.left) || 0,
    top: parseFloat(layer.style.top) || 0,
    zoom,
  };
}

interface CablesLayerProps {
  cables: Cable[];
  equipment: PlacedEquipment[];
  racks: Rack[];
}

const CablesLayer: React.FC<CablesLayerProps> = ({ cables, equipment, racks }) => {
  const logicOn = useProjectStore((s) => s.getCurrentDesign()?.logicMode === 'logic');
  const cableLogic = useLogicStore((s) => s.result?.cables);
  const {
    portsDisplayMode,
    badgeAnchor,
    canvasZoom,
    gridSize,
    iconGridCells,
    enableAdvancedCableRun,
    isCableRunMode,
    setHoveredCableId,
    setSelectedCable,
    setCablePortEditorOpen,
    setCableConfigOpen,
    setSelectedEquipment,
    setSelectedRack,
    setSelectedRackEquipment,
    toggleCableExistingStatus,
    updateCableRun,
    setHoveredPortIdDuringCableDrag,
  } = useProjectStore(
    useShallow((s) => {
      return {
        portsDisplayMode: s.portsDisplayMode,
        badgeAnchor: s.badgeAnchor as BadgeAnchor,
        canvasZoom: s.canvasZoom,
        gridSize: s.gridSize,
        iconGridCells: s.iconGridCells,
        enableAdvancedCableRun: Boolean(s.featureFlags?.enableAdvancedCableRun),
        isCableRunMode: s.isCableRunMode,
        setHoveredCableId: s.setHoveredCableId,
        setSelectedCable: s.setSelectedCable,
        setCablePortEditorOpen: s.setCablePortEditorOpen,
        setCableConfigOpen: s.setCableConfigOpen,
        setSelectedEquipment: s.setSelectedEquipment,
        setSelectedRack: s.setSelectedRack,
        setSelectedRackEquipment: s.setSelectedRackEquipment,
        toggleCableExistingStatus: s.toggleCableExistingStatus,
        updateCableRun: s.updateCableRun,
        setHoveredPortIdDuringCableDrag: s.setHoveredPortIdDuringCableDrag,
      };
    })
  );

  const isAdvancedMode = enableAdvancedCableRun && portsDisplayMode === 'full';
  const iconSize = canvasIconSizePx(gridSize, iconGridCells);
  const iconHalf = iconSize / 2;

  const geomCtx = useMemo<CableGeometryContext>(
    () => ({
      equipment,
      racks,
      portsDisplayMode,
      badgeAnchor,
      canvasZoom,
      enableAdvancedCableRun,
      iconSize,
    }),
    [equipment, racks, portsDisplayMode, badgeAnchor, canvasZoom, enableAdvancedCableRun, iconSize]
  );

  const resolved = useMemo<ResolvedRow[]>(() => {
    const rows: ResolvedRow[] = [];
    for (const cable of cables) {
      const ends = resolveCableEndpoints(cable, geomCtx);
      if (!ends) continue;
      const base = getCableColor(cable.type, cable.isExisting);
      const logicStatus = logicOn ? cableLogic?.[cable.id]?.status : undefined;
      const color =
        logicStatus === 'error' ? '#ef4444' : logicStatus === 'warning' ? '#f59e0b' : base;
      rows.push({
        cable,
        x1: ends.source.x,
        y1: ends.source.y,
        x2: ends.dest.x,
        y2: ends.dest.y,
        fromPlacedId: ends.sourceData.placedId,
        toPlacedId: ends.destData.placedId,
        sourcePanelOn: isPortPanelVisible(ends.sourceData.placedId, equipment, portsDisplayMode),
        destPanelOn: isPortPanelVisible(ends.destData.placedId, equipment, portsDisplayMode),
        fromPort: extractPortNumber(cable.from),
        toPort: extractPortNumber(cable.to),
        color,
        strokeWidth: 2.25,
        hoverWidth: isAdvancedMode ? 2.75 : 3,
        opacity: isAdvancedMode ? 0.5 : 0.7,
        hoverOpacity: isAdvancedMode ? 0.5 : 1,
        flexi: isFlexiRouting(cable.routing),
      });
    }
    return rows;
  }, [cables, geomCtx, equipment, portsDisplayMode, isAdvancedMode, logicOn, cableLogic]);

  const layerRef = useRef<HTMLDivElement>(null);
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const numberOverlayRef = useRef<HTMLDivElement>(null);
  const overlayEnabledRef = useRef(false);
  const [viewportEl, setViewportEl] = useState<HTMLElement | null>(null);
  const resolvedRef = useRef(resolved);
  const geomCtxRef = useRef(geomCtx);
  const hoverIdRef = useRef<string | null>(null);
  const elsMap = useRef(new Map<string, LineEls>());
  const dragHandlersRef = useRef<{ move: ((e: MouseEvent) => void) | null; up: ((e: MouseEvent) => void) | null }>({
    move: null,
    up: null,
  });
  const currentHoveredPortRef = useRef<{ equipmentId: string; portId: string; position?: { x: number; y: number } } | null>(null);
  const isDragActiveRef = useRef(false);
  const loopRafRef = useRef(0);

  if (!isDragActiveRef.current) {
    resolvedRef.current = resolved;
  }
  geomCtxRef.current = geomCtx;
  overlayEnabledRef.current = true;

  const redraw = useCallback(() => {
    paintCables(canvasRef.current, resolvedRef.current, hoverIdRef.current);
    syncPortNumberOverlay(
      numberOverlayRef.current,
      document.querySelector('[data-canvas-viewport="true"]') as HTMLElement | null,
      overlayEnabledRef.current
    );
  }, []);

  const followLiveIcons = useCallback(() => {
    const live: Record<string, { x: number; y: number }> = {};
    document.querySelectorAll<HTMLElement>('[data-equipment-id]').forEach((el) => {
      const id = el.dataset.equipmentId;
      if (!id) return;
      const x = parseFloat(el.style.left);
      const y = parseFloat(el.style.top);
      if (Number.isFinite(x) && Number.isFinite(y)) live[id] = { x, y };
    });

    const matchLive = (placedId: string, endpointId: string) => {
      if (live[placedId]) return live[placedId];
      if (live[endpointId]) return live[endpointId];
      for (const id of Object.keys(live)) {
        if (endpointId === id || endpointId.startsWith(`${id}:`) || endpointId.startsWith(`${id}:port:`)) {
          return live[id];
        }
      }
      return undefined;
    };

    if (Object.keys(live).length > 0) {
      for (const row of resolvedRef.current) {
        const fromLive = matchLive(row.fromPlacedId, row.cable.from);
        const toLive = matchLive(row.toPlacedId, row.cable.to);
        if (!fromLive && !toLive) continue;

        if (row.sourcePanelOn || row.destPanelOn) {
          const ends = resolveCableEndpoints(row.cable, {
            ...geomCtxRef.current,
            draggedPositions: live,
          });
          if (ends) {
            row.x1 = ends.source.x;
            row.y1 = ends.source.y;
            row.x2 = ends.dest.x;
            row.y2 = ends.dest.y;
          }
        } else {
          if (fromLive) {
            row.x1 = fromLive.x + NAME_BADGE_FALLBACK_WIDTH / 2;
            row.y1 = fromLive.y + NAMEPLATE_ROW_PX / 2;
          }
          if (toLive) {
            row.x2 = toLive.x + NAME_BADGE_FALLBACK_WIDTH / 2;
            row.y2 = toLive.y + NAMEPLATE_ROW_PX / 2;
          }
        }

        const els = elsMap.current.get(row.cable.id);
        if (els) {
          setLineCoords(
            els,
            row.x1,
            row.y1,
            row.x2,
            row.y2,
            row.flexi,
            rowAttachmentPoints(row, iconHalf)
          );
        }
      }
    }

    redraw();
  }, [redraw, iconHalf]);

  const refreshEls = useCallback(() => {
    const layer = layerRef.current;
    const next = new Map<string, LineEls>();
    if (layer) {
      for (const row of resolvedRef.current) {
        const id = row.cable.id;
        next.set(id, {
          hit: layer.querySelector(`[data-cable-id="${CSS.escape(id)}"][data-role="hit"]`) as LineEls['hit'],
          fromCircle: layer.querySelector(`[data-cable-id="${CSS.escape(id)}"][data-end="from"]`) as HTMLElement | null,
          toCircle: layer.querySelector(`[data-cable-id="${CSS.escape(id)}"][data-end="to"]`) as HTMLElement | null,
        });
      }
    }
    elsMap.current = next;
  }, []);

  const getEls = useCallback((cableId: string): LineEls => {
    return elsMap.current.get(cableId) || { hit: null, fromCircle: null, toCircle: null };
  }, []);

  const applyHover = useCallback((cableId: string | null) => {
    hoverIdRef.current = cableId;
    redraw();
  }, [redraw]);

  useLayoutEffect(() => {
    setViewportEl(document.querySelector('[data-canvas-viewport="true"]') as HTMLElement | null);
  }, []);

  useLayoutEffect(() => {
    if (isDragActiveRef.current) {
      followLiveIcons();
      return;
    }
    refreshEls();
    redraw();
    applyHover(useProjectStore.getState().hoveredCableId);
  }, [resolved, viewportEl, applyHover, refreshEls, redraw, followLiveIcons]);

  useEffect(() => {
    const stopLoop = () => {
      if (loopRafRef.current) {
        cancelAnimationFrame(loopRafRef.current);
        loopRafRef.current = 0;
      }
    };

    const tick = () => {
      followLiveIcons();
      loopRafRef.current = requestAnimationFrame(tick);
    };
    loopRafRef.current = requestAnimationFrame(tick);

    const setInert = (active: boolean) => {
      isDragActiveRef.current = active;
      const layer = layerRef.current;
      if (!layer) return;
      layer.querySelectorAll<HTMLElement | SVGElement>('[data-role="hit"], [data-cable-endpoint]').forEach((el) => {
        el.style.display = active ? 'none' : '';
        // Flexi SVGs must stay pointer-events:none so only the stroke path captures.
        if (el.getAttribute('data-flexi-hit') === 'true') return;
        el.style.pointerEvents = active ? 'none' : 'auto';
      });
    };

    if (useProjectStore.getState().isEquipmentDragActive) {
      setInert(true);
    }

    const unsubStore = useProjectStore.subscribe((state, prev) => {
      if (state.hoveredCableId !== prev.hoveredCableId) {
        applyHover(state.hoveredCableId);
      }
      if (state.isEquipmentDragActive !== prev.isEquipmentDragActive) {
        setInert(state.isEquipmentDragActive);
      }
    });

    const unsubDrag = subscribeEquipmentDrag(() => {
      followLiveIcons();
    });

    return () => {
      unsubStore();
      unsubDrag();
      stopLoop();
      restorePortNumberSources();
    };
  }, [applyHover, followLiveIcons]);

  useEffect(() => {
    return () => {
      if (dragHandlersRef.current.move) {
        document.removeEventListener('mousemove', dragHandlersRef.current.move);
      }
      if (dragHandlersRef.current.up) {
        document.removeEventListener('mouseup', dragHandlersRef.current.up);
        document.removeEventListener('pointerup', dragHandlersRef.current.up);
        document.removeEventListener('pointercancel', dragHandlersRef.current.up);
      }
    };
  }, []);

  const findPortAtPosition = useCallback(
    (clientX: number, clientY: number): { equipmentId: string; portId: string; position?: { x: number; y: number } } | null => {
      const draggedCircle = layerRef.current?.querySelector('[data-cable-endpoint="true"][data-dragging="true"]');
      const originalDisplay = draggedCircle ? (draggedCircle as HTMLElement).style.display : null;
      if (draggedCircle) {
        (draggedCircle as HTMLElement).style.display = 'none';
      }

      const elementUnderMouse = document.elementFromPoint(clientX, clientY);

      if (draggedCircle && originalDisplay !== null) {
        (draggedCircle as HTMLElement).style.display = originalDisplay;
      } else if (draggedCircle) {
        (draggedCircle as HTMLElement).style.display = '';
      }

      if (!elementUnderMouse) return null;
      const portElement = elementUnderMouse.closest('.port');
      if (!portElement) return null;
      const portStage = portElement.closest('[data-port-stage-for]');
      if (!portStage) return null;
      const equipmentId = (portStage as HTMLElement).getAttribute('data-port-stage-for');
      if (!equipmentId) return null;
      const eq = equipment.find((e) => e.id === equipmentId);
      if (!eq) return null;
      const portNumberAttr = portElement.getAttribute('data-port-number');
      const portKindAttr = portElement.getAttribute('data-port-kind') || undefined;
      const portNumber = portNumberAttr
        ? parseInt(portNumberAttr, 10)
        : parseInt(portElement.querySelector('.number')?.textContent || '0', 10);
      if (Number.isNaN(portNumber) || portNumber === 0) return null;

      const portId = `${equipmentId}:port:${portNumber}${
        portKindAttr ? `:kind:${encodeURIComponent(portKindAttr)}` : ''
      }`;
      const computedPos = getPortPosition(
        portId,
        { equipment: eq.equipment, position: eq.position, placedId: eq.id },
        geomCtxRef.current
      );
      if (computedPos) {
        return { equipmentId, portId, position: computedPos };
      }

      const layerEl = getPanningLayer();
      if (!layerEl) return null;
      const layerRect = layerEl.getBoundingClientRect();
      const portRect = portElement.getBoundingClientRect();
      return {
        equipmentId,
        portId,
        position: {
          x: (portRect.left + portRect.width / 2 - layerRect.left) / canvasZoom,
          y: (portRect.top + portRect.height / 2 - layerRect.top) / canvasZoom,
        },
      };
    },
    [equipment, canvasZoom]
  );

  const handleCableClick = useCallback(
    (e: React.MouseEvent, cable: Cable) => {
      if (isCableRunMode) return;
      e.stopPropagation();
      setSelectedEquipment(null);
      setSelectedRack(null);
      setSelectedRackEquipment('', null);
      setSelectedCable(cable);
      setCableConfigOpen(false);
      setCablePortEditorOpen(true);
    },
    [isCableRunMode, setSelectedEquipment, setSelectedRack, setSelectedRackEquipment, setSelectedCable, setCablePortEditorOpen, setCableConfigOpen]
  );

  const handleEndpointDragStart = useCallback(
    (e: React.MouseEvent, cable: Cable, endpoint: 'from' | 'to', color: string) => {
      e.stopPropagation();
      e.preventDefault();
      if (!isAdvancedMode) return;

      if (dragHandlersRef.current.move) {
        document.removeEventListener('mousemove', dragHandlersRef.current.move);
      }
      if (dragHandlersRef.current.up) {
        document.removeEventListener('mouseup', dragHandlersRef.current.up);
        document.removeEventListener('pointerup', dragHandlersRef.current.up);
        document.removeEventListener('pointercancel', dragHandlersRef.current.up);
      }

      const panningLayer = getPanningLayer();
      if (!panningLayer) return;

      const els = getEls(cable.id);
      const circle = endpoint === 'from' ? els.fromCircle : els.toCircle;
      paintEndpointCircle(circle, { dragging: true, hoverPort: false, color });

      // Truthy for the whole drag so equipment can clear leftover CSS hover even when
      // the pointer is not over a port (empty-space hit tests used to set this to null).
      const DRAG_ACTIVE = '__endpoint_drag__';
      let dragLive = true;
      let hitRaf = 0;
      let pendingClient: { x: number; y: number } | null = null;
      let lastHoverPortId: string | null = DRAG_ACTIVE;
      setHoveredPortIdDuringCableDrag(DRAG_ACTIVE);
      const restX = endpoint === 'from'
        ? Number(els.hit?.dataset.x2 ?? 0)
        : Number(els.hit?.dataset.x1 ?? 0);
      const restY = endpoint === 'from'
        ? Number(els.hit?.dataset.y2 ?? 0)
        : Number(els.hit?.dataset.y1 ?? 0);

      const applyHitTest = (clientX: number, clientY: number) => {
        if (!dragLive) return;
        const port = findPortAtPosition(clientX, clientY);
        const live = getEls(cable.id);
        const row = resolvedRef.current.find((r) => r.cable.id === cable.id);
        if (port && port.position) {
          currentHoveredPortRef.current = port;
          if (endpoint === 'from') {
            if (row) { row.x1 = port.position.x; row.y1 = port.position.y; }
            setLineCoords(live, port.position.x, port.position.y, restX, restY, row?.flexi);
          } else {
            if (row) { row.x2 = port.position.x; row.y2 = port.position.y; }
            setLineCoords(live, restX, restY, port.position.x, port.position.y, row?.flexi);
          }
          paintEndpointCircle(endpoint === 'from' ? live.fromCircle : live.toCircle, {
            dragging: true,
            hoverPort: true,
            color,
          });
          redraw();
          if (lastHoverPortId !== port.portId) {
            lastHoverPortId = port.portId;
            setHoveredPortIdDuringCableDrag(port.portId);
          }
        } else {
          const rect = panningLayer.getBoundingClientRect();
          const x = (clientX - rect.left) / canvasZoom;
          const y = (clientY - rect.top) / canvasZoom;
          currentHoveredPortRef.current = null;
          if (endpoint === 'from') {
            if (row) { row.x1 = x; row.y1 = y; }
            setLineCoords(live, x, y, restX, restY, row?.flexi);
          } else {
            if (row) { row.x2 = x; row.y2 = y; }
            setLineCoords(live, restX, restY, x, y, row?.flexi);
          }
          paintEndpointCircle(endpoint === 'from' ? live.fromCircle : live.toCircle, {
            dragging: true,
            hoverPort: false,
            color,
          });
          redraw();
          if (lastHoverPortId !== DRAG_ACTIVE) {
            lastHoverPortId = DRAG_ACTIVE;
            setHoveredPortIdDuringCableDrag(DRAG_ACTIVE);
          }
        }
      };

      const handleMouseMove = (moveEvent: MouseEvent) => {
        if (!dragLive) return;
        pendingClient = { x: moveEvent.clientX, y: moveEvent.clientY };
        if (hitRaf) return;
        hitRaf = requestAnimationFrame(() => {
          hitRaf = 0;
          if (dragLive && pendingClient) applyHitTest(pendingClient.x, pendingClient.y);
        });
      };

      const handleMouseUp = (upEvent: MouseEvent) => {
        if (!dragLive) return;
        dragLive = false;
        if (hitRaf) cancelAnimationFrame(hitRaf);
        hitRaf = 0;
        pendingClient = null;
        let port = currentHoveredPortRef.current;
        if (!port || !port.portId) {
          port = findPortAtPosition(upEvent.clientX, upEvent.clientY);
          if (!port) {
            for (let offsetX = -10; offsetX <= 10 && !port; offsetX += 5) {
              for (let offsetY = -10; offsetY <= 10; offsetY += 5) {
                port = findPortAtPosition(upEvent.clientX + offsetX, upEvent.clientY + offsetY);
                if (port?.portId) break;
              }
            }
          }
        }

        if (port?.portId) {
          updateCableRun(cable.id, endpoint === 'from' ? { from: port.portId } : { to: port.portId });
        }

        currentHoveredPortRef.current = null;
        document.removeEventListener('mousemove', handleMouseMove);
        document.removeEventListener('mouseup', handleMouseUp);
        document.removeEventListener('pointerup', handleMouseUp);
        document.removeEventListener('pointercancel', handleMouseUp);
        dragHandlersRef.current.move = null;
        dragHandlersRef.current.up = null;
        document.body.style.userSelect = '';
        document.body.style.cursor = '';
        paintEndpointCircle(getEls(cable.id)[endpoint === 'from' ? 'fromCircle' : 'toCircle'], {
          dragging: false,
          hoverPort: false,
          color,
        });
        setHoveredPortIdDuringCableDrag(null);
      };

      dragHandlersRef.current.move = handleMouseMove;
      dragHandlersRef.current.up = handleMouseUp;
      document.addEventListener('mousemove', handleMouseMove);
      document.addEventListener('mouseup', handleMouseUp);
      document.addEventListener('pointerup', handleMouseUp);
      document.addEventListener('pointercancel', handleMouseUp);
      document.body.style.userSelect = 'none';
      document.body.style.cursor = 'grabbing';
    },
    [isAdvancedMode, canvasZoom, findPortAtPosition, getEls, redraw, setHoveredPortIdDuringCableDrag, updateCableRun]
  );

  if (resolved.length === 0) return null;

  return (
    <>
      {viewportEl &&
        createPortal(
          <>
            <canvas
              ref={canvasRef}
              className="absolute inset-0"
              style={{ pointerEvents: 'none', zIndex: isAdvancedMode ? 30 : 2 }}
            />
            <div
              ref={numberOverlayRef}
              data-port-number-overlay="true"
              className="absolute inset-0 pointer-events-none"
              style={{ zIndex: isAdvancedMode ? 31 : 3 }}
            />
          </>,
          viewportEl
        )}

      <div
        ref={layerRef}
        className="absolute"
        style={{
          top: 0,
          left: 0,
          width: 0,
          height: 0,
          overflow: 'visible',
          pointerEvents: 'none',
          zIndex: isAdvancedMode ? 30 : 2,
        }}
      >
        {resolved.map((row) => {
          const { cable } = row;
          const attach = rowAttachmentPoints(row, iconHalf);
          const flexiMetrics = row.flexi
            ? flexiHitMetrics(attach.from.x, attach.from.y, attach.to.x, attach.to.y)
            : null;
          const hoverHandlers = {
            onMouseEnter: () => {
              if (useProjectStore.getState().isEquipmentDragActive) return;
              setHoveredCableId(cable.id);
            },
            onMouseLeave: () => {
              if (useProjectStore.getState().hoveredCableId === cable.id) {
                setHoveredCableId(null);
              }
            },
            onClick: (e: React.MouseEvent) => handleCableClick(e, cable),
            onContextMenu: (e: React.MouseEvent) => {
              e.preventDefault();
              toggleCableExistingStatus(cable.id);
            },
          };
          return (
            <React.Fragment key={cable.id}>
              {row.flexi && flexiMetrics ? (
                <svg
                  data-cable-id={cable.id}
                  data-role="hit"
                  data-flexi-hit="true"
                  data-x1={attach.from.x}
                  data-y1={attach.from.y}
                  data-x2={attach.to.x}
                  data-y2={attach.to.y}
                  viewBox={`0 0 ${flexiMetrics.width} ${flexiMetrics.height}`}
                  style={flexiHitStyle(attach.from.x, attach.from.y, attach.to.x, attach.to.y)}
                >
                  <path
                    d={flexiMetrics.d}
                    fill="none"
                    stroke="rgba(255,255,255,0.01)"
                    strokeWidth={FLEXI_HIT_STROKE}
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    style={{ pointerEvents: 'stroke', cursor: 'pointer' }}
                    {...hoverHandlers}
                  />
                </svg>
              ) : (
                <div
                  data-cable-id={cable.id}
                  data-role="hit"
                  data-x1={attach.from.x}
                  data-y1={attach.from.y}
                  data-x2={attach.to.x}
                  data-y2={attach.to.y}
                  style={segmentStyle(attach.from.x, attach.from.y, attach.to.x, attach.to.y, 14)}
                  {...hoverHandlers}
                />
              )}
              {isAdvancedMode && row.fromPort !== null && row.sourcePanelOn && (
                <div
                  data-cable-id={cable.id}
                  data-end="from"
                  data-cable-endpoint="true"
                  title="Drag to reattach"
                  style={{
                    position: 'absolute',
                    left: attach.from.x - 7,
                    top: attach.from.y - 7,
                    width: 14,
                    height: 14,
                    borderRadius: '50%',
                    background: 'transparent',
                    opacity: 1,
                    border: 'none',
                    boxSizing: 'border-box',
                    pointerEvents: 'auto',
                    cursor: 'grab',
                    zIndex: 1,
                  }}
                  onMouseDown={(e) => handleEndpointDragStart(e, cable, 'from', row.color)}
                />
              )}
              {isAdvancedMode && row.toPort !== null && row.destPanelOn && (
                <div
                  data-cable-id={cable.id}
                  data-end="to"
                  data-cable-endpoint="true"
                  title="Drag to reattach"
                  style={{
                    position: 'absolute',
                    left: attach.to.x - 7,
                    top: attach.to.y - 7,
                    width: 14,
                    height: 14,
                    borderRadius: '50%',
                    background: 'transparent',
                    opacity: 1,
                    border: 'none',
                    boxSizing: 'border-box',
                    pointerEvents: 'auto',
                    cursor: 'grab',
                    zIndex: 1,
                  }}
                  onMouseDown={(e) => handleEndpointDragStart(e, cable, 'to', row.color)}
                />
              )}
            </React.Fragment>
          );
        })}
      </div>
    </>
  );
};

export default React.memo(CablesLayer);
